import React from 'react';
import { clsx, createUseStyles } from '@pushpay/styles';
import { CarouselProvider, useCarousel } from './carousel-context';
const useStyles = createUseStyles({
  root: ({
    width
  }) => ({
    width,
    overflowX: 'hidden'
  }),
  carouselItem: ({
    transitionDurationMs
  }) => ({
    flex: 1,
    visibility: 'hidden',
    transition: `visibility ${transitionDurationMs}ms`
  }),
  selectedItem: () => ({
    visibility: 'visible'
  }),
  container: ({
    carouselIndex,
    childrenCount,
    invalidIndex,
    noChildren,
    transitionDurationMs
  }) => {
    const invalidState = noChildren || invalidIndex;
    const childrenContainerWidth = invalidState ? '100%' : `${childrenCount}00%`;
    const offsetPercentage = (carouselIndex / childrenCount * -100).toFixed(2);
    const transform = invalidState ? '0%' : `${offsetPercentage}%`;
    return {
      display: 'flex',
      flexDirection: 'row',
      height: '100%',
      position: 'relative',
      transform: `translateX(${transform})`,
      transition: `transform ${transitionDurationMs}ms`,
      width: childrenContainerWidth
    };
  },
  errorText: {
    flex: 1,
    textAlign: 'center'
  }
});
export const CarouselContent = ({
  carouselItems,
  classes: classesProp,
  className: classNameProp,
  'data-pp-at-target': targetId,
  selectedIndex,
  transitionDurationMs = 300,
  width = '100%',
  ...containerProps
}) => {
  const {
    selectedCarouselIndex
  } = useCarousel();
  const carouselIndex = selectedIndex || selectedCarouselIndex;
  const childrenCount = carouselItems.length;
  const noChildren = childrenCount === 0;
  const invalidIndex = carouselIndex < 0 || carouselIndex > childrenCount - 1;
  const classes = useStyles(classesProp, {
    childrenCount,
    invalidIndex,
    noChildren,
    carouselIndex,
    transitionDurationMs,
    width
  });
  const rootClasses = clsx(classes.root, classNameProp);

  const renderCarouselContent = () => {
    if (noChildren && process.env.NODE_ENV === 'development') {
      return /*#__PURE__*/React.createElement("div", {
        className: classes.errorText
      }, "Carousel has no items!");
    }

    if (invalidIndex && process.env.NODE_ENV === 'development') {
      return /*#__PURE__*/React.createElement("div", {
        className: classes.errorText
      }, `The defaultIndex of (${carouselIndex}) is outside the allowed range of [0..${childrenCount - 1}]!`);
    }

    return carouselItems.map((item, i) => /*#__PURE__*/React.createElement("div", {
      className: clsx(classes.carouselItem, i === carouselIndex && classes.selectedItem),
      "data-pp-at-target": `${targetId}-${i}` // eslint-disable-next-line react/no-array-index-key
      ,
      key: i,
      role: "listitem"
    }, item));
  };

  return /*#__PURE__*/React.createElement("div", {
    className: rootClasses
  }, /*#__PURE__*/React.createElement("div", Object.assign({
    role: "list",
    className: classes.container,
    "data-pp-at-target": targetId
  }, containerProps), renderCarouselContent()));
};
export const Carousel = ({
  carouselItems,
  defaultIndex = 0,
  ...rest
}) => /*#__PURE__*/React.createElement(CarouselProvider, {
  carouselItemsCount: carouselItems.length,
  defaultIndex: defaultIndex
}, /*#__PURE__*/React.createElement(CarouselContent, Object.assign({
  carouselItems: carouselItems
}, rest)));